'use client';

import {
  inferFailedToolCallLabel,
  resolveToolCallLabels,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import {
  friendlyToolFailureExplanation,
  isFailedToolCallMessage,
} from './failed-tool-call-display';
import { FailedToolCallRow } from './FailedToolCallRow';
import { VStack, HStack } from '@terra/ui-styled-system';
import { useMemo, useState, createElement, type ReactNode } from 'react';
import { UserIcon } from '@terra/ui-icons';
import { MetaDefault } from '../../../components/typography';
import { Switch } from '../../../components/switch';
import { useCredentialsContext } from '../credentials';
import { useChatHar } from '../har/ChatHarProvider';
import { TraceLine } from './trace';
import { containsHITLRejectionMarker } from '../utils/hitl-rejection-marker';
import { isSkillReadToolCall } from './tool-call-skill-read';
import { buildToolHeaderMetadata } from './tool-call-update-header-metadata';
import { useToolHarSummary } from './tool-call-update-har-summary';
import { InlineToolCallExpand } from './InlineToolCallExpand';
import { MinimalToolCard } from './MinimalToolCard';
import { prepareToolResultData } from './ToolCallsRenderers/content-detection';
import { getSkillDisplayNameFromPath } from './ToolCallsRenderers/skill-display';
import { HttpArtifact } from '../../artifacts/http/HttpArtifact';
import { ToolCallContentRenderer } from './ToolCallContentRenderer';
import { getToolConfig } from './tool-config';
import type {
  OnOpenChatObject,
  OnOpenHttpInMainContent,
  SkillDraftReference,
} from './hitl-types';
import { useBackgroundExecution } from './BackgroundExecutionContext';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import { readPortalObjectResult } from '../utils/portal-object-result';
import { resolveToolCallErrorDisplay } from '../utils/resolve-tool-call-error-display';
import { PortalObjectToolResultRow } from './PortalObjectToolResultRow';
import {
  AutoApprovalBadge,
  readAutoApproval,
} from './tools/shared/auto-approval-badge';

type ToolCallUpdateCardProps = {
  dataMessage: ToolCallGenericMessage;
  onSSESubscribe?: SseSubscribe;
  hideRequestIfShownInHITL?: boolean;
  hitlDecisionType?: 'approve' | 'reject' | 'edit';
  onOpenHttpInMainContent?: OnOpenHttpInMainContent;
  onOpenBrowserStreamInMainContent?: (data: { streamingId: string }) => void;
  autoCollapseWhenComplete?: boolean;
  forceExpanded?: boolean;
  isGroupRunning?: boolean;
  onOpenObject?: OnOpenChatObject;
  nestedContent?: ReactNode;
  nestedSummary?: string;
  skillDraftReference?: SkillDraftReference;
  onEditAskUserAnswer?: (args: {
    toolCallId?: string;
    toolCallPartId?: string;
  }) => void;
  onAbortSubagent?: (event: { toolCallId: string }) => void;
  focusedSubagentToolCallId?: string;
  isLatestToolCall?: boolean;
  revealHiddenOutput?: boolean;
};

export function ToolCallUpdateCard({
  dataMessage,
  onSSESubscribe,
  hideRequestIfShownInHITL,
  hitlDecisionType,
  onOpenBrowserStreamInMainContent,
  autoCollapseWhenComplete,
  forceExpanded,
  isGroupRunning,
  onOpenObject,
  nestedContent,
  nestedSummary,
  skillDraftReference,
  onEditAskUserAnswer,
  onAbortSubagent,
  focusedSubagentToolCallId,
  isLatestToolCall,
  revealHiddenOutput,
}: ToolCallUpdateCardProps) {
  const { credentialsPreview } = useCredentialsContext();
  const { fetchHarData } = useChatHar();
  const backgroundExecution = useBackgroundExecution();
  const toolCallData = dataMessage;
  const resolvedErrorDisplay = resolveToolCallErrorDisplay({
    error: toolCallData.error,
    metadata: toolCallData.metadata,
  });
  const expansionKey = toolCallData.toolCallId ?? toolCallData.id;
  const autoApproval = readAutoApproval(toolCallData.autoApproval);
  const autoApprovalBadge = autoApproval ? (
    <AutoApprovalBadge autoApproval={autoApproval} subject="action" />
  ) : undefined;
  const [isRawDataExpanded, setIsRawDataExpanded] = useState(false);
  const toolName = toolCallData?.toolName;
  const config = getToolConfig(toolName);
  const toolIcon = createElement(config.icon, { size: 12 });
  const isRunning =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';
  const labels = resolveToolCallLabels({
    toolName,
    message: toolCallData.message,
    labelPresent: toolCallData.labelPresent,
    labelPast: toolCallData.labelPast,
  });
  const displayLabel = isRunning ? labels.labelPresent : labels.labelPast;
  const isCompleted = toolCallData?.status === 'completed';
  const timing = toolCallData?.timing;
  const credentialId = useMemo(
    () => config.extractCredentialId?.(toolCallData?.input),
    [config, toolCallData?.input],
  );
  const credentialPreview = useMemo(() => {
    if (!credentialId || !credentialsPreview.length) return undefined;

    return credentialsPreview.find((c) => c.id === credentialId);
  }, [credentialId, credentialsPreview]);
  const metadata = useMemo(() => {
    if (!toolCallData || !config.extractMetadata) return [];

    return config.extractMetadata({
      input: toolCallData.input,
      output: toolCallData.output,
      status: toolCallData.status,
    });
  }, [toolCallData, config]);
  const isStarted = toolCallData.status === 'started';
  const hasContentDefault = config.hideInput
    ? !!(toolCallData?.output || config.isStreaming)
    : config.showOutputOnly
      ? !!(toolCallData?.output || isStarted) // Show loading state while in progress
      : !!(toolCallData?.input || toolCallData?.output || config.isStreaming);
  const hasContent = config.hasExpandableContent
    ? config.hasExpandableContent({
        input: toolCallData?.input,
        output: toolCallData?.output,
        status: toolCallData?.status,
      })
    : hasContentDefault;
  const shouldHideRejectedHITLContent = hitlDecisionType === 'reject';
  const canShowContent = hasContent && !shouldHideRejectedHITLContent;
  const customActions = useMemo(() => {
    if (!config.getCustomActions || !toolCallData) return [];

    return config.getCustomActions({
      input: toolCallData.input,
      output: toolCallData.output,
      isCompleted,
    });
  }, [config, toolCallData, isCompleted]);
  const portalObjectResult = useMemo(() => {
    if (!onOpenObject) {
      return null;
    }

    return readPortalObjectResult({
      input: toolCallData.input,
      output: toolCallData.output,
      contentType: config.contentType,
      isCompleted,
    });
  }, [
    config.contentType,
    isCompleted,
    onOpenObject,
    toolCallData.input,
    toolCallData.output,
  ]);

  const harSummary = useToolHarSummary({
    enabled:
      isRawDataExpanded &&
      Boolean(toolCallData?.toolCallId) &&
      toolCallData?.status === 'completed',
    toolCallId: toolCallData?.toolCallId,
    fetchHarData,
  });
  const intelligenceOutputData = useMemo(
    () =>
      toolCallData?.output
        ? prepareToolResultData({
            content: toolCallData.output,
            toolName: toolCallData.toolName,
          })
        : null,
    [toolCallData?.output, toolCallData?.toolName],
  );
  const skillRead = isSkillReadToolCall({
    toolName,
    input: toolCallData?.input,
    output: toolCallData?.output,
  });
  const skillName = skillRead.isSkillRead
    ? (getSkillDisplayNameFromPath(skillRead.targetPath) ?? 'skill')
    : null;

  if (!toolCallData) return null;
  if (toolCallData.status === 'aborted') return null;
  if (
    hitlDecisionType === 'reject' ||
    containsHITLRejectionMarker(toolCallData.output)
  ) {
    return null;
  }

  if (
    !config.rendersFailureCard &&
    isFailedToolCallMessage({
      status: toolCallData.status,
      type: toolCallData.type,
    })
  ) {
    const explanation = friendlyToolFailureExplanation(resolvedErrorDisplay);
    const title = skillRead.isSkillRead
      ? inferFailedToolCallLabel(`Reading skill ${skillName ?? ''}...`).trim()
      : labels.labelFailed;

    if (revealHiddenOutput) {
      return <MetaDefault color="fg.muted">{explanation}</MetaDefault>;
    }

    return <FailedToolCallRow title={title} explanation={explanation} />;
  }

  if (skillRead.isSkillRead) {
    const skillLabel = isRunning
      ? `Reading skill ${skillName ?? ''}`
      : `Read skill ${skillName ?? ''}`;

    return (
      <TraceLine
        title={skillLabel.trim()}
        status={isRunning ? 'running' : undefined}
      />
    );
  }

  if (hideRequestIfShownInHITL && portalObjectResult && onOpenObject) {
    return (
      <PortalObjectToolResultRow
        label={displayLabel}
        portalObjectResult={portalObjectResult}
        onOpenObject={onOpenObject}
      />
    );
  }

  if (config.renderCard) {
    const renderedCard = config.renderCard({
      toolCallData: {
        input: toolCallData.input,
        output: toolCallData.output,
        status: toolCallData.status,
        error: resolvedErrorDisplay,
        toolCallId: toolCallData.toolCallId,
        id: toolCallData.id,
        timing: toolCallData.timing,
        progressEvents: toolCallData.progressEvents,
      },
      toolIcon,
      toolName: config.name,
      runningText: config.runningText,
      labelPresent: labels.labelPresent,
      labelPast: labels.labelPast,
      autoCollapseWhenComplete,
      forceExpanded:
        forceExpanded === true ||
        (Boolean(focusedSubagentToolCallId) &&
          focusedSubagentToolCallId === toolCallData.toolCallId),
      expandWhileRunning: config.expandWhileRunning,
      isGroupRunning,
      expansionKey,
      hitlDecisionType,
      headerActions: autoApprovalBadge,
      onSSESubscribe,
      onOpenObject,
      getBackgroundExecutionByToolCallId:
        backgroundExecution?.getBackgroundExecutionByToolCallId,
      nestedContent,
      nestedSummary,
      skillDraftReference,
      onEditAskUserAnswer,
      onAbortSubagent,
      isLatestToolCall,
    });

    if (renderedCard === null && !revealHiddenOutput) {
      return null;
    }

    if (renderedCard !== undefined && renderedCard !== null) {
      return renderedCard;
    }
  }

  if (config.cardType === 'inline' && !config.isStreaming) {
    return (
      <InlineToolCallExpand
        toolName={toolName || 'unknown'}
        input={toolCallData.input}
        output={toolCallData.output}
        status={toolCallData.status}
        displayLabel={displayLabel}
        hasStreamedLabel={Boolean(
          toolCallData.labelPresent || toolCallData.labelPast,
        )}
        canShowContent={canShowContent}
        isSkillReadToolCall={skillRead.isSkillRead}
        intelligenceOutputData={intelligenceOutputData}
        error={resolvedErrorDisplay}
        onOpenObject={onOpenObject}
        liveLabel={config.runningText}
        expansionKey={expansionKey}
      />
    );
  }

  const shouldAutoExpand = false;
  const shouldExpandWhileRunning =
    config.expandWhileRunning === true && isRunning;
  const canShowHttpTimeline =
    config.supportsHttpTimeline && !!toolCallData.toolCallId;
  const isToolCompleted = toolCallData.status === 'completed';
  const httpViewToggle = config.supportsHttpTimeline ? (
    <HStack
      gap={1.5}
      alignItems="center"
      flexShrink={0}
      onClick={(e) => e.stopPropagation()}
    >
      <MetaDefault
        color={
          canShowHttpTimeline && isToolCompleted ? 'fg.muted' : 'fg.disabled'
        }
      >
        HTTP
      </MetaDefault>
      <Switch
        size="sm"
        checked={isRawDataExpanded}
        onCheckedChange={(e) => setIsRawDataExpanded(e.checked)}
        disabled={!canShowHttpTimeline || !isToolCompleted}
      />
    </HStack>
  ) : null;
  // Build header metadata - includes credential, method/status, HAR summary
  const headerMetadata = buildToolHeaderMetadata({
    credentialPreview,
    credentialIcon: <UserIcon size={12} />,
    metadata,
    harSummary: canShowHttpTimeline ? harSummary : null,
  });

  return (
    <MinimalToolCard
      icon={toolIcon}
      toolName={displayLabel}
      status={toolCallData.status}
      metadata={headerMetadata}
      error={resolvedErrorDisplay}
      timing={canShowHttpTimeline && harSummary ? undefined : timing}
      defaultExpanded={shouldAutoExpand || shouldExpandWhileRunning}
      forceExpanded={forceExpanded}
      autoCollapseWhenComplete={autoCollapseWhenComplete}
      customActions={customActions}
      hitlStatus={hitlDecisionType}
      headerActions={
        autoApprovalBadge && httpViewToggle ? (
          <HStack gap={1} alignItems="center">
            {autoApprovalBadge}
            {httpViewToggle}
          </HStack>
        ) : (
          (autoApprovalBadge ?? httpViewToggle)
        )
      }
      expansionKey={expansionKey}
    >
      {canShowContent ? (
        <VStack alignItems="start" gap={3} width="100%">
          {isRawDataExpanded &&
          canShowHttpTimeline &&
          toolCallData.toolCallId ? (
            <HttpArtifact
              toolCallId={toolCallData.toolCallId}
              toolType={config.harToolType ?? 'HTTP'}
              showSummaryHeader={true}
            />
          ) : (
            <ToolCallContentRenderer
              input={toolCallData.input}
              output={toolCallData.output}
              progressEvents={toolCallData.progressEvents}
              toolName={toolName}
              hideRequestIfShownInHITL={hideRequestIfShownInHITL}
              toolCallId={toolCallData.toolCallId}
              toolCallIdFallback={toolCallData.id}
              isStarted={isRunning}
              onSSESubscribe={onSSESubscribe}
              onOpenBrowserStreamInMainContent={
                onOpenBrowserStreamInMainContent
              }
              onOpenObject={onOpenObject}
            />
          )}
        </VStack>
      ) : null}
    </MinimalToolCard>
  );
}
